import { useEffect, useState } from 'react';
import { ModalLayout, ModalSize, ModalTemplate, modal } from '@ardoq/modal';
import { Select } from '@ardoq/select';
import {
  CheckboxGroup,
  CheckboxGroupItem,
  CheckboxGroupValue,
  FormSize,
} from '@ardoq/forms';
import { dispatchAction, connect } from '@ardoq/rxbeach';
import {
  CopyWsViewModel,
  TargetOrg,
  copyError,
  copySuccess,
  copyWorkspacesToOrg,
  copyWsViewModel$,
  resetCopyWs,
} from './copyws$';
import { WarningNotification } from '@ardoq/status-ui';
import {
  onFirstAction,
  subscribeToActionOnce,
} from 'streams/utils/streamUtils';
import styled from 'styled-components';
import { t } from '@ardoq/i18n';
import { ArdoqId } from '@ardoq/api-types';

type CopyWsToOrgModalProps = CopyWsViewModel & {
  onClose: VoidFunction;
  preSelectedWsIds?: ArdoqId[];
};

const CopyWsToOrgModal = ({
  onClose,
  organizations,
  workspaces,
  error,
  i18nLocale,
  preSelectedWsIds,
}: CopyWsToOrgModalProps) => {
  const hasPreSelectedWorkspaces =
    preSelectedWsIds !== undefined && preSelectedWsIds.length > 0;
  const [targetOrg, setTargetOrg] = useState<TargetOrg>();
  const [selectedWsIds, setSelectedWsIds] = useState(
    new Set<string>(hasPreSelectedWorkspaces ? preSelectedWsIds : [])
  );
  const [isInProgress, setInProgress] = useState<boolean>(false);
  useEffect(() => {
    const subscription = onFirstAction(copySuccess, onClose);
    return () => subscription.unsubscribe();
  }, [onClose]);
  return (
    <ModalTemplate
      modalSize={ModalSize.M}
      headerText={t(
        i18nLocale,
        'copyWsToOrg:header',
        'Copy workspaces to another organization'
      )}
      isPrimaryButtonDisabled={!targetOrg?.value || selectedWsIds.size === 0}
      primaryButtonText={t(i18nLocale, 'copyWsToOrg:primaryButton', 'Copy')}
      onPrimaryButtonClick={() => {
        setInProgress(true);
        dispatchAction(
          copyWorkspacesToOrg({ targetOrg: targetOrg!, selectedWsIds })
        );
        subscribeToActionOnce(copyError, () => {
          setInProgress(false);
        });
      }}
      isInProgress={isInProgress}
      secondaryButtonText={t(
        i18nLocale,
        'copyWsToOrg:secondaryButton',
        'Cancel'
      )}
      onSecondaryButtonClick={() => {
        dispatchAction(resetCopyWs());
        onClose();
      }}
    >
      <ModalLayout>
        {error && <WarningNotification> {error} </WarningNotification>}
        <SelectWrapper>
          <Select
            value={targetOrg}
            label={t(
              i18nLocale,
              'copyWsToOrg:organizationsLabel',
              'Target organization'
            )}
            options={organizations.map(({ name, label }) => ({
              label: name,
              value: label,
            }))}
            onChange={selectedValue => setTargetOrg(selectedValue as TargetOrg)}
            i18nLocale={i18nLocale}
          />
        </SelectWrapper>
        {!hasPreSelectedWorkspaces && (
          <CheckboxGroup
            label={t(i18nLocale, 'copyWsToOrg:workspacesLabel', 'Workspaces')}
            onValueChange={(selectedIds: CheckboxGroupValue) => {
              setSelectedWsIds(
                new Set([...selectedIds.map(c => c.toString())])
              );
            }}
          >
            {workspaces.map(({ id, name }) => (
              <CheckboxGroupItem key={id} value={id}>
                {name}
              </CheckboxGroupItem>
            ))}
          </CheckboxGroup>
        )}
      </ModalLayout>
    </ModalTemplate>
  );
};
const ConnectedCopyWsToOrgModal = connect(CopyWsToOrgModal, copyWsViewModel$);

const SelectWrapper = styled.div`
  max-width: ${FormSize.DEFAULT};
`;

type CopyWsToOrgModalOptions = {
  preSelectedWsIds?: ArdoqId[];
};

export const copyWsToOrgModal = (options: CopyWsToOrgModalOptions = {}) => {
  modal(
    resolve => (
      <ConnectedCopyWsToOrgModal
        onClose={() => resolve(false)}
        preSelectedWsIds={options.preSelectedWsIds}
      />
    ),
    {
      shouldCloseOnBackdropClick: false,
      shouldCloseOnEscapeKey: false,
    }
  );
};
